// @architect-approved: INFRASTRUCTURE - SkybridgeTram is a navigation infra component, not a DB-registered component
/**
 * [CONTAINER-START]
 * CONTAINER-TYPE: navigation
 * CONTAINER-CLASS: infrastructure
 * ROUTE: /skybridge
 * NAV-LABEL: Skybridge Tram System
 * TG-SOLUTION-ID: 100
 * ASSOCIATION-TYPE: none (public)
 * DESCRIPTION: Platform switcher - Navigate between Techgacity Center and Life Center Village
 * DEPENDENCIES: TramStops.config
 * CONTAINER-STATUS: mvp-production
 * CONTAINER-VERSION: 1.0.0
 * [CONTAINER-END]
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Card, CardContent, Grid, Button, Chip } from '@mui/material';
import TramIcon from '@mui/icons-material/Tram';
import { getActiveTramStops } from './TramStops.config';

const SkybridgeTram = () => {
    const navigate = useNavigate();
    const activeTramStops = getActiveTramStops();

    const handleNavigateToStop = (welcomeCenterRoute: string) => {
        navigate(welcomeCenterRoute);
    };

    const getPlatformColor = (platform: string) => {
        switch (platform) {
            case 'commercial':
                return 'primary';
            case 'public':
                return 'success';
            case 'mixed':
                return 'secondary';
            default:
                return 'default';
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 8, mb: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <TramIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
                <Typography variant="h2" component="h1" gutterBottom>
                    Skybridge Tram System
                </Typography>
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    Navigate Between Platforms
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        mt: 2,
                    }}
                >
                    Select your destination to board the tram
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {activeTramStops.map((stop) => (
                    <Grid
                        key={stop.id}
                        size={{
                            xs: 12,
                            md: 6,
                        }}
                    >
                        <Card
                            sx={{
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': { boxShadow: 6 },
                                border: 2,
                                borderColor: `${getPlatformColor(stop.platform)}.main`,
                            }}
                            onClick={() => handleNavigateToStop(stop.welcomeCenterRoute)}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant="h4" component="h2">
                                        {stop.name}
                                    </Typography>
                                    <Chip
                                        label={stop.platform.toUpperCase()}
                                        color={getPlatformColor(stop.platform) as any}
                                        size="small"
                                    />
                                </Box>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'text.secondary',
                                        mb: 3,
                                    }}
                                >
                                    {stop.description}
                                </Typography>

                                <Typography variant="subtitle2" gutterBottom>
                                    Floors:
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                                    {stop.floors.map((floor, index) => (
                                        <Typography
                                            component="li"
                                            variant="body2"
                                            key={index}
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 0.5,
                                            }}
                                        >
                                            {floor}
                                        </Typography>
                                    ))}
                                </Box>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    color={getPlatformColor(stop.platform) as any}
                                    startIcon={<TramIcon />}
                                >
                                    Board Tram to {stop.name}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'info.light', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                    Expanding Network
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    Future tram stops: Innovation Campus, Education Center, Healthcare Hub, Supply
                    Chain Command Center, and more.
                </Typography>
            </Box>
        </Container>
    );
};

export default SkybridgeTram;
